import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '@/components/shared/Navigation';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Users, DollarSign } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { adminMemberTypesApi } from '@/lib/api';

interface MemberType {
  id: number;
  name: string;
  description: string;
  monthly_fee: string; // API returns this as string
  created_at?: string;
  updated_at?: string;
  members_count?: number;
}

interface MemberTypeFormData {
  name: string;
  description: string;
  monthly_fee: string;
}

const AdminMemberTypes = () => {
  const [memberTypes, setMemberTypes] = useState<MemberType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingMemberType, setEditingMemberType] = useState<MemberType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<MemberTypeFormData>({
    name: '',
    description: '',
    monthly_fee: ''
  });

  const fetchMemberTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await adminMemberTypesApi.getAll();
      
      // Handle the API response format: {status: "success", data: [...]}
      let types: MemberType[] = [];
      if (Array.isArray(response)) {
        types = response;
      } else if (response && Array.isArray(response.data)) {
        types = response.data;
      } else {
        console.warn('Unexpected response format:', response);
        types = [];
      }
      
      setMemberTypes(types);
    } catch (error) {
      console.error('Error fetching member types:', error);
      toast({
        title: "Error",
        description: "Failed to fetch member types",
        variant: "destructive"
      });
      setMemberTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMemberTypes();
  }, [fetchMemberTypes]);

  const handleCreateMemberType = async () => {
    if (!formData.name.trim() || !formData.monthly_fee.trim()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const memberTypeData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        monthly_fee: parseFloat(formData.monthly_fee)
      };
      
      console.log('Attempting to create member type:', memberTypeData);
      console.log('API Base URL:', import.meta.env.VITE_API_BASE_URL || "https://4b08-197-186-9-241.ngrok-free.app/api");
      console.log('Auth token exists:', !!localStorage.getItem('auth_token'));
      
      await adminMemberTypesApi.create(memberTypeData);
      
      toast({
        title: "Success",
        description: "Member type created successfully"
      });
      
      setIsCreateDialogOpen(false);
      resetForm();
      fetchMemberTypes();
    } catch (error: any) {
      console.error('Detailed error creating member type:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: error.config
      });
      
      let errorMsg = 'Failed to create member type';
      
      if (error.response?.status === 405) {
        errorMsg = 'Create member type feature is not implemented in the backend API yet. This is a backend development task.';
      } else if (error.response?.status === 401) {
        errorMsg = 'Unauthorized. Please login again.';
      } else if (error.response?.status === 403) {
        errorMsg = 'Forbidden. Admin access required.';
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateMemberType = async () => {
    if (!editingMemberType || !formData.name.trim() || !formData.monthly_fee.trim()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const memberTypeData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        monthly_fee: parseFloat(formData.monthly_fee)
      };
      
      await adminMemberTypesApi.update(editingMemberType.id, memberTypeData);
      
      toast({
        title: "Success",
        description: "Member type updated successfully"
      });
      
      setIsEditDialogOpen(false);
      setEditingMemberType(null);
      resetForm();
      fetchMemberTypes();
    } catch (error: any) {
      console.error('Detailed error updating member type:', {
        status: error.response?.status,
        data: error.response?.data,
        memberTypeId: editingMemberType?.id
      });
      
      let errorMsg = 'Failed to update member type';
      if (error.response?.status === 405) {
        errorMsg = 'Update member type feature is not implemented in the backend API yet.';
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMemberType = async (id: number, name: string) => {
    try {
      await adminMemberTypesApi.delete(id);
      
      toast({
        title: "Success",
        description: `Member type "${name}" deleted successfully`
      });
      
      fetchMemberTypes();
    } catch (error: any) {
      console.error('Detailed error deleting member type:', {
        status: error.response?.status,
        data: error.response?.data,
        memberTypeId: id
      });
      
      let errorMsg = 'Failed to delete member type';
      if (error.response?.status === 405) {
        errorMsg = 'Delete member type feature is not implemented in the backend API yet.';
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  const openEditDialog = useCallback((memberType: MemberType) => {
    setEditingMemberType(memberType);
    setFormData({
      name: memberType.name,
      description: memberType.description,
      monthly_fee: memberType.monthly_fee
    });
    setIsEditDialogOpen(true);
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      monthly_fee: ''
    });
  }, []);

  const formatCurrency = useCallback((amount: string) => {
    try {
      return parseFloat(amount).toLocaleString();
    } catch {
      return amount;
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center">
            <LoadingSpinner />
            <span className="ml-2">Loading member types...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Member Types</h1>
            <p className="text-gray-600 mt-2">Manage membership types and their fees</p>
          </div>
          
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setIsCreateDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Member Type
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Member Type</DialogTitle>
                <DialogDescription>
                  Add a new membership type with its monthly fee and description.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Premium Member"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the benefits and features of this membership type"
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="monthly_fee">Monthly Fee (TZS) *</Label>
                  <Input
                    id="monthly_fee"
                    type="number"
                    step="0.01"
                    value={formData.monthly_fee}
                    onChange={(e) => setFormData(prev => ({ ...prev, monthly_fee: e.target.value }))}
                    placeholder="50000.00"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 mt-6">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateMemberType} disabled={isSubmitting}>
                  {isSubmitting ? <LoadingSpinner size="sm" /> : 'Create'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* API Status Notice */}
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Backend API Development Needed
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Currently displaying existing member types from the database. 
                  Create, Update, and Delete operations require backend API implementation 
                  (POST, PUT, DELETE endpoints for /api/member-types).
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {memberTypes.map((memberType) => (
            <Card key={memberType.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{memberType.name}</CardTitle>
                    <CardDescription className="mt-1">
                      TZS {formatCurrency(memberType.monthly_fee)} / month
                    </CardDescription>
                  </div>
                  <Badge variant="secondary">
                    <Users className="w-3 h-3 mr-1" />
                    {memberType.members_count || 0}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4 min-h-[3rem]">
                  {memberType.description || 'No description provided'}
                </p>
                
                <div className="flex justify-between items-center">
                  <div className="flex items-center text-sm text-gray-500">
                    <DollarSign className="w-4 h-4 mr-1" />
                    TZS {formatCurrency(memberType.monthly_fee)}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openEditDialog(memberType)}
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Member Type</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{memberType.name}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteMemberType(memberType.id, memberType.name)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {memberTypes.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Member Types</h3>
              <p className="text-gray-600 mb-4">No member types found in the system.</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Member Type
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Member Type</DialogTitle>
              <DialogDescription>
                Update the membership type details.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Premium Member"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the benefits and features of this membership type"
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-monthly_fee">Monthly Fee (TZS) *</Label>
                <Input
                  id="edit-monthly_fee"
                  type="number"
                  step="0.01"
                  value={formData.monthly_fee}
                  onChange={(e) => setFormData(prev => ({ ...prev, monthly_fee: e.target.value }))}
                  placeholder="50000.00"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => { setIsEditDialogOpen(false); setEditingMemberType(null); }}>
                Cancel
              </Button>
              <Button onClick={handleUpdateMemberType} disabled={isSubmitting}>
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Update'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminMemberTypes;